import React from "react";
import { Link } from "react-router-dom";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import MarketingBackdrop, { panelClass, softPanelClass, ctaSectionClass, pillClass } from "../components/MarketingBackdrop";

const pillars = [
  {
    title: "CSV import today",
    desc: "Upload the stock export you already have, map it once, then prepare a clean count session without rebuilding spreadsheets every day.",
  },
  {
    title: "Prepared count sessions",
    desc: "Morpheus converts messy stock data into guided count sheets, live sessions, flags, notes, and clear progress tracking per site.",
  },
  {
    title: "Variance intelligence",
    desc: "See system stock vs counted stock, cost impact, gains, losses, waste, clean lines, variance lines, and repeat problem categories.",
  },
  {
    title: "Management-ready PDFs",
    desc: "Export clean count reports and order sheets that owners, managers, and operators can actually read, share, and act on.",
  },
];

const securityPoints = [
  "Organization-scoped Firestore data separation",
  "Authenticated access layer before workspace entry",
  "Profile PIN layer for count operators and admins",
  "Site-based profile access for controlled counting",
  "Audit-friendly session history, archives, flags, and notes",
];

const roadmap = [
  {
    title: "Now",
    text: "CSV import, mapping, category rules, prepared data, live sessions, archive, PDF exports, profiles, and site-level control.",
  },
  {
    title: "Next",
    text: "Manual import for businesses without a POS export, so teams can still structure count data without needing integrations first.",
  },
  {
    title: "Later",
    text: "POS integrations so stock positions can flow into Morpheus automatically when a client is ready for deeper automation.",
  },
];

const workflow = [
  "Import CSV",
  "Map columns",
  "Prepare data",
  "Start count",
  "Flag variances",
  "Export report",
];

export default function MorpheusCountLanding() {
  return (
    <MarketingBackdrop>
      <SiteHeader active="products" />
      <main className="relative pt-28">
        <section className="px-6 py-16 md:py-24">
          <div className="max-w-7xl mx-auto grid lg:grid-cols-[1.05fr_0.95fr] gap-8 items-start">
            <div>
              <div className={`inline-flex items-center gap-2 px-4 py-2 ${pillClass}`}>
                <span className="h-2.5 w-2.5 rounded-full bg-cyan-300 shadow-[0_0_14px_rgba(34,211,238,0.85)]" />
                <span className="text-[0.72rem] uppercase tracking-[0.28em] text-cyan-100">MatrixOS • Stock Intelligence</span>
              </div>

              <div className="mt-6 text-sm uppercase tracking-[0.28em] text-cyan-300/90">Morpheus Count</div>
              <h1 className="mt-4 max-w-5xl text-4xl md:text-6xl font-extrabold leading-[0.98] tracking-[-0.03em] text-white">
                Turn stock counts into trusted operational intelligence.
              </h1>
              <p className="mt-6 max-w-3xl text-lg md:text-xl leading-8 text-slate-200/90">
                Morpheus Count helps teams move from scattered CSV exports, manual checking, and uncertain stock movement into prepared sessions, controlled counts, variance exposure, and clean reports.
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/login" state={{ preferredApp: "mc" }} className="rounded-full bg-cyan-400 px-6 py-3 text-sm font-semibold text-[#071120] hover:bg-cyan-300 transition">
                  Sign in to Morpheus
                </Link>
                <Link to="/book-demo" className="rounded-full border border-cyan-300/40 bg-cyan-400/5 px-6 py-3 text-sm font-semibold text-white hover:bg-cyan-400/10 transition">
                  Book Demo
                </Link>
                <Link to="/products" className="rounded-full border border-white/12 px-6 py-3 text-sm font-semibold text-slate-100 hover:border-cyan-300/40 transition">
                  Back to products
                </Link>
              </div>

              <div className="mt-10 grid sm:grid-cols-2 gap-4 max-w-4xl">
                {pillars.map((item) => (
                  <div key={item.title} className={`${softPanelClass} p-5`}>
                    <div className="text-cyan-300 font-semibold">{item.title}</div>
                    <div className="mt-2 text-sm leading-6 text-slate-300">{item.desc}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className={`${panelClass} p-5 md:p-6`}>
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="text-xs uppercase tracking-[0.25em] text-cyan-300">Count session flow</div>
                  <div className="mt-2 text-2xl font-bold text-white">From CSV to control</div>
                </div>
                <div className="rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3 py-1 text-xs font-semibold text-emerald-200">
                  Phase 1 live
                </div>
              </div>

              <div className="mt-6 grid gap-3">
                {workflow.map((item, index) => (
                  <div key={item} className="flex items-center gap-4 rounded-2xl border border-white/8 bg-white/[0.035] p-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-cyan-300/30 bg-cyan-400/10 text-sm font-bold text-cyan-100">
                      {String(index + 1).padStart(2, "0")}
                    </div>
                    <div className="text-slate-100 font-semibold">{item}</div>
                  </div>
                ))}
              </div>

              <div className="mt-6 rounded-[1.5rem] border border-cyan-300/15 bg-cyan-400/[0.06] p-5">
                <div className="text-xs uppercase tracking-[0.25em] text-cyan-200">Trust layer</div>
                <div className="mt-3 text-2xl font-bold text-white">Your stock data stays inside your organization workspace.</div>
                <p className="mt-3 text-sm leading-6 text-slate-300">
                  Morpheus is designed around authenticated access, organization-scoped data, profile selection, PIN control, and count history that supports accountability.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="px-6 py-8 md:py-12">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-[0.95fr_1.05fr] gap-6">
            <div className={`${panelClass} p-8`}>
              <div className="text-sm uppercase tracking-[0.22em] text-cyan-300">Why it matters</div>
              <h2 className="mt-4 text-3xl md:text-4xl font-bold text-white">Counting is not the hard part. Trusting the result is.</h2>
              <p className="mt-5 text-slate-300 leading-8">
                A stock count becomes valuable when managers can see what changed, which lines are clean, which lines need attention, and where money is being exposed. Morpheus Count turns the count into a decision layer, not just a list of products.
              </p>
            </div>
            <div className={`${softPanelClass} p-8`}>
              <div className="text-sm uppercase tracking-[0.22em] text-cyan-300">Security and control</div>
              <div className="mt-4 grid gap-4">
                {securityPoints.map((item) => (
                  <div key={item} className="rounded-2xl border border-white/8 bg-black/15 px-4 py-4 text-slate-200/90">
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="px-6 py-16">
          <div className="max-w-6xl mx-auto">
            <div className="text-center max-w-3xl mx-auto">
              <div className="text-xs uppercase tracking-[0.28em] text-cyan-300">Build path</div>
              <h2 className="mt-4 text-3xl md:text-5xl font-extrabold text-white">Built for CSV now. Ready for manual capture and POS integrations later.</h2>
              <p className="mt-4 text-slate-300 leading-8">
                Not every business has the same starting point. Some have POS exports, some need manual import first, and some will later connect directly into their POS ecosystem.
              </p>
            </div>

            <div className="mt-10 grid md:grid-cols-3 gap-6">
              {roadmap.map((item, index) => (
                <TimelineStep key={item.title} number={String(index + 1).padStart(2, "0")} title={item.title} desc={item.text} />
              ))}
            </div>
          </div>
        </section>

        <section className="px-6 pb-20">
          <div className={`max-w-5xl mx-auto ${ctaSectionClass} px-8 py-10 md:px-10 md:py-12 text-center`}>
            <div className="text-xs uppercase tracking-[0.28em] text-cyan-300">Morpheus Count • MatrixOS</div>
            <h2 className="mt-4 text-3xl md:text-4xl font-bold text-white">Give your stock count a brain, not just a spreadsheet.</h2>
            <p className="mt-4 max-w-3xl mx-auto text-slate-200/90 leading-8">
              Import the data, prepare the session, count with control, and export the report. Morpheus Count is the stock intelligence layer for operators who need clarity fast.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/login" state={{ preferredApp: "mc" }} className="rounded-full bg-cyan-400 px-6 py-3 text-sm font-semibold text-[#071120] hover:bg-cyan-300 transition">Enter Morpheus</Link>
              <Link to="/contact" className="rounded-full border border-white/12 px-6 py-3 text-sm font-semibold text-white hover:border-cyan-300/40 transition">Talk to us</Link>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </MarketingBackdrop>
  );
}

function TimelineStep({ number, title, desc }) {
  return (
    <div className={`${panelClass} p-6`}>
      <div className="text-cyan-300 text-sm font-semibold tracking-[0.22em]">{number}</div>
      <div className="mt-4 text-2xl font-bold text-white">{title}</div>
      <div className="mt-3 text-slate-300 leading-7">{desc}</div>
    </div>
  );
}
